import { Fragment, useCallback, useEffect } from "react";
import { Outlet, Route, Routes, useLocation, useNavigate } from "react-router-dom";
import { APP_URLS } from "../constants/appConstants";
import NotFoundPage from "../pages/NotFoundPage/NotFoundPage";
import TrainingCompetency from "../pages/TrainingCompetency/TrainingCompetency";
import DashboardPage from "../pages/Dashboard/Dashboard";
import { useAppDispatch, useAppSelector } from "../store/hooks";
import { verifySessionThunk } from "../store/auth/thunkMiddlewares";
import { openSessionEndedStream } from "../utils/sessionEnded";
import { START_UP_DELAY, VERIFY_SESSION_PERIOD } from "../constants/authentication";
import LoadingPage from "../Components/LoadingPage/LoadingPage";
import Header from "../Components/Header/Header";
import CallBackSignPage from "../pages/SignInPage/CallBackSignPage";
import MicrosoftCallBackSignPage from "../pages/SignInPage/MicrosoftCallBackSignPage";
import AuthenticationRoute from "../Components/AuthenticationRoute/AuthenticationRoute";
import SignInPage from "../pages/SignInPage/SignInPage";
import TrainingManagement from "../pages/TrainingManagement/TrainingManagement";
import CAPAManagement from "../pages/CAPAManagement/CAPAManagement";
import SOPReview from "../pages/SOPReview/SOPReview";
import PurchaseAcceptance from "../pages/PurchaseAcceptance/PurchaseAcceptance";
import NonconformanceAndInvestigation from "../pages/NonconformanceAndInvestigation/NonconformanceAndInvestigation";
import InternalAudit from "../pages/InternalAudit/InternalAudit";
import ManagementReviews from "../pages/ManagementReviews/ManagementReviews";
import DeviceManagement from "../pages/DeviceManagement/DeviceManagement";
import NumberOfCreatedAndResolved from "../pages/NumberOfCreatedAndResolved/NumberOfCreatedAndResolved";
import TrainingKPIPage from "../pages/TrainingKPIPage/TrainingKPIPage";

const RouterWrapper = () => {
    const auth = useAppSelector((state: any) => state.auth);
    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const location = useLocation();
    const checkAuthenticationStatus = useCallback(() => {
        dispatch(verifySessionThunk(navigate));
        setTimeout(() => {
            return checkAuthenticationStatus();
        }, VERIFY_SESSION_PERIOD);
    }, []);

    useEffect(() => {
        if (location.pathname === "/") {
            navigate(APP_URLS.DASHBOARD);
        }
    }, [location, navigate]);

    useEffect(() => {
        setTimeout(checkAuthenticationStatus, START_UP_DELAY);
    }, []);

    // Signed out the moment an administrator ends this session in the admin app.
    useEffect(() => {
        if (!auth.adminEventsUrl) return undefined;
        return openSessionEndedStream(auth.adminEventsUrl);
    }, [auth.adminEventsUrl]);

    // A tab that was in the background (or a laptop that slept) may have missed
    // the event, so check the session again as soon as the tab is shown.
    useEffect(() => {
        const onVisibilityChange = () => {
            if (document.visibilityState === "visible") {
                dispatch(verifySessionThunk(navigate));
            }
        };
        document.addEventListener("visibilitychange", onVisibilityChange);
        return () => document.removeEventListener("visibilitychange", onVisibilityChange);
    }, [dispatch, navigate]);

    if (auth.isLoading) {
        return <LoadingPage />;
    }

    return (
        <Fragment>
            {auth.isLoggedIn && <Header />}
            <Outlet />
        </Fragment>
    );
};

const Router = () => {
    const auth = useAppSelector((state: any) => state.auth);
    const isAuthenticated = auth.isLoggedIn;
    return (
        <Routes>
            <Route path={APP_URLS.SIGN_IN_CALLBACK} element={<CallBackSignPage />} />
            <Route path={APP_URLS.MS_SIGN_IN_CALLBACK} element={<MicrosoftCallBackSignPage />} />
            <Route path={"/"} element={<RouterWrapper />}>
                <Route path={`${APP_URLS.SOP_REVIEW}`} element={<SOPReview />} />
        
                <Route path={`${APP_URLS.TRAINING_COMPETENCY}`} element={<TrainingKPIPage />} />
                <Route path={`${APP_URLS.TRAINING_MANAGEMENT}/:period`} element={<TrainingManagement />} />
                <Route path={`${APP_URLS.TRAINING_MANAGEMENT}`} element={<TrainingManagement />} />
         
                <Route path={`${APP_URLS.NONCONFORMANCE_AND_INVESTIGATION}/:period`} element={<NonconformanceAndInvestigation />} />
                <Route path={`${APP_URLS.NONCONFORMANCE_AND_INVESTIGATION}`} element={<NonconformanceAndInvestigation />} />
                <Route path={`${APP_URLS.CAPA_MANAGEMENT}`} element={<CAPAManagement />} />
                <Route path={`${APP_URLS.CAPA_MANAGEMENT}/:period`} element={<CAPAManagement />} />
                <Route path={`${APP_URLS.PURCHASE_ACCEPTANCE}`} element={<PurchaseAcceptance />} />
                <Route path={`${APP_URLS.PURCHASE_ACCEPTANCE}/:period`} element={<PurchaseAcceptance />} />
                <Route path={`${APP_URLS.INTERNAL_AUDIT}`} element={<InternalAudit />} />
                <Route path={`${APP_URLS.INTERNAL_AUDIT}/:year`} element={<InternalAudit />} />
                <Route path={`${APP_URLS.MANAGEMENT_REVIEWS}`} element={<ManagementReviews />} />
                <Route path={`${APP_URLS.MANAGEMENT_REVIEWS}/:year`} element={<ManagementReviews />} />
                <Route path={`${APP_URLS.DEVICE_MANAGEMENT}`} element={<DeviceManagement deviceManagementData={[]} />} />
                <Route path={`${APP_URLS.NUMBER_OF_CREATED_AND_RESOLVED}`} element={<NumberOfCreatedAndResolved />} />
                <Route path={`${APP_URLS.NUMBER_OF_CREATED_AND_RESOLVED}/:period`} element={<NumberOfCreatedAndResolved />} />
                <Route path={`${APP_URLS.DASHBOARD}`} element={<DashboardPage />} />
                <Route path={"training-kpi"} element={<TrainingKPIPage />} />
                <Route path={"*"} element={<NotFoundPage />} />
                <Route
                    path={APP_URLS.SIGN_IN}
                    element={<AuthenticationRoute path={APP_URLS.DASHBOARD} authed={isAuthenticated} component={<SignInPage />} />}
                />
            </Route>
        </Routes>
    );
};
export default Router;
